import { useState } from 'react';
import { X, User, Mail, Phone, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { OtpVerificationModal } from './OtpVerificationModal';
import { otpService } from '../../services/otpService';
import { captureError } from '../../lib/errorHandler';

interface EditProfileSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userData: {
    name: string;
    email: string;
    phone: string;
    isEmailVerified?: boolean;
  };
  onSave: (data: { name: string }) => Promise<string | null>;
  onEmailUpdated?: () => void;
}

export function EditProfileSheet({ open, onOpenChange, userData, onSave, onEmailUpdated }: EditProfileSheetProps) {
  const [name, setName] = useState(userData.name);
  const [email, setEmail] = useState(userData.email);
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // OTP state for email update
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isSendingEmailOtp, setIsSendingEmailOtp] = useState(false);
  const [isVerifyingEmailOtp, setIsVerifyingEmailOtp] = useState(false);

  if (!open) return null;

  const hasEmailChanged = email.trim() !== userData.email;
  const hasNameChanged = name.trim() !== userData.name;

  const validateName = (value: string) => {
    if (!value.trim()) return 'Name is required';
    if (value.trim().length < 2) return 'Name must be at least 2 characters';
    if (!/^[a-zA-Z\s]+$/.test(value)) return 'Name can only contain letters';
    return '';
  };

  const validateEmail = (value: string) => {
    if (!value.trim()) return ''; // Empty is ok (clearing email)
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email';
    return '';
  };

  const handleSave = async () => {
    const nameErr = validateName(name);
    setNameError(nameErr);
    setNameTouched(true);

    if (nameErr) return;

    // If email changed, validate and trigger OTP flow
    if (hasEmailChanged && email.trim()) {
      const eErr = validateEmail(email);
      if (eErr) {
        setEmailError(eErr);
        return;
      }

      // Save name first if changed, then start email OTP
      if (hasNameChanged) {
        setIsLoading(true);
        setApiError(null);
        const errorMessage = await onSave({ name });
        setIsLoading(false);
        if (errorMessage) {
          setApiError(errorMessage);
          return;
        }
      }

      // Send email update OTP
      setIsSendingEmailOtp(true);
      setApiError(null);
      try {
        await otpService.sendEmailUpdateOtp(email.trim());
        setShowOtpModal(true);
        setOtpError(null);
      } catch (err: any) {
        const msg = err?.response?.data?.message || 'Failed to send verification code';
        setApiError(msg);
      } finally {
        setIsSendingEmailOtp(false);
      }
      return;
    }

    // Only name changed (or email cleared)
    if (!hasNameChanged && !hasEmailChanged) {
      onOpenChange(false);
      return;
    }

    setIsLoading(true);
    setApiError(null);

    const errorMessage = await onSave({ name });
    setIsLoading(false);

    if (errorMessage) {
      setApiError(errorMessage);
    } else {
      onOpenChange(false);
    }
  };

  const handleEmailOtpVerified = async (code: string) => {
    setIsVerifyingEmailOtp(true);
    setOtpError(null);
    try {
      await otpService.verifyEmailUpdateOtp(email.trim(), code);
      setShowOtpModal(false);
      onEmailUpdated?.();
      onOpenChange(false);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Verification failed';
      setOtpError(msg);
    } finally {
      setIsVerifyingEmailOtp(false);
    }
  };

  const handleResendEmailOtp = async () => {
    try {
      await otpService.sendEmailUpdateOtp(email.trim());
    } catch (error) {
      captureError(error, 'EditProfileSheet.resendEmailOtp', { email: email.trim() });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[2000] flex items-end sm:items-center justify-center">
      <div className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Profile</h2>
            <button
              onClick={() => onOpenChange(false)}
              className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Full Name *
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <User className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameTouched) {
                    setNameError(validateName(e.target.value));
                  }
                }}
                onBlur={() => {
                  setNameTouched(true);
                  setNameError(validateName(name));
                }}
                className={`w-full h-14 pl-12 pr-4 rounded-xl border-2 ${
                  nameTouched && nameError
                    ? 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800'
                    : 'border-gray-200 dark:border-gray-700 focus:border-[#8bc43e] dark:bg-gray-800 dark:text-white'
                } outline-none transition-colors`}
                placeholder="Enter your full name"
              />
            </div>
            {nameTouched && nameError && (
              <div className="flex items-center gap-2 mt-2 text-red-600 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{nameError}</span>
              </div>
            )}
          </div>

          {/* Email Field (Editable) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Email Address
              {userData.isEmailVerified && !hasEmailChanged && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-normal">
                  <CheckCircle2 className="w-3 h-3" /> Verified
                </span>
              )}
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <Mail className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(validateEmail(e.target.value));
                }}
                className={`w-full h-14 pl-12 pr-4 rounded-xl border-2 ${
                  emailError
                    ? 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800'
                    : 'border-gray-200 dark:border-gray-700 focus:border-[#8bc43e] dark:bg-gray-800 dark:text-white'
                } outline-none transition-colors`}
                placeholder="Enter your email"
              />
            </div>
            {emailError && (
              <div className="flex items-center gap-2 mt-2 text-red-600 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{emailError}</span>
              </div>
            )}
            {hasEmailChanged && email.trim() && !emailError && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                A verification code will be sent to this email
              </p>
            )}
          </div>

          {/* Phone Field (Read-only) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Phone Number
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <Phone className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="w-full h-14 pl-12 pr-4 rounded-xl border-2 border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex items-center">
                <span className="text-gray-500 dark:text-gray-400">{userData.phone}</span>
              </div>
            </div>
          </div>

          {/* Info Note */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <span className="font-semibold">Note:</span> Phone number cannot be changed. Contact support if you need to update it.
            </p>
          </div>
        </div>

        {/* API Error Display */}
        {apiError && (
          <div className="px-6 pb-4">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-600 dark:text-red-400">{apiError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] rounded-b-3xl">
          <div className="flex gap-3">
            <Button
              onClick={() => onOpenChange(false)}
              disabled={isLoading || isSendingEmailOtp}
              className="flex-1 h-14 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white rounded-xl font-semibold"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isLoading || isSendingEmailOtp}
              className="flex-1 h-14 bg-[#8bc43e] hover:bg-[#7ab32e] text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading || isSendingEmailOtp ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{isSendingEmailOtp ? 'Sending...' : 'Saving...'}</span>
                </div>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* OTP Verification Modal for Email Update */}
      <OtpVerificationModal
        open={showOtpModal}
        onOpenChange={setShowOtpModal}
        email={email.trim()}
        purpose="email_update"
        onVerified={handleEmailOtpVerified}
        onResend={handleResendEmailOtp}
        isVerifying={isVerifyingEmailOtp}
        error={otpError}
      />
    </div>
  );
}
